/**
 * Tabs, sub-tabs, the filter bar with saved views, the timeline, and the Entity 360 frame.
 *
 * Entity 360 is the shape most of this ERP will take: one record, many angles, each a tab (BRIEF §22–24).
 * The registry already names the tab sets — Person has 22, Customer 18, Chiller 12 — so the frame here
 * reads them rather than each screen re-listing them.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Filter, X } from 'lucide-react';
import { Button, Panel, SectionLabel, cx } from './primitives';
import { dateTime as fmtDateTime } from './format';

/* ── Tabs ────────────────────────────────────────────────────────────────────────────────────────── */

export interface TabDef {
  id: string;
  label: string;
  /** Shown beside the label. `undefined` means "not counted yet", which is not the same as zero. */
  count?: number | undefined;
  disabled?: boolean;
}

export function Tabs({
  tabs, active, onChange, variant = 'primary',
}: {
  tabs: readonly TabDef[];
  active: string;
  onChange: (id: string) => void;
  variant?: 'primary' | 'sub';
}) {
  return (
    <div
      role="tablist"
      className={cx(
        'flex items-center gap-1 overflow-x-auto',
        variant === 'primary' ? 'border-b border-line px-1' : 'px-1 py-1',
      )}
    >
      {tabs.map((tab) => {
        const selected = tab.id === active;
        return (
          <button
            key={tab.id}
            role="tab"
            type="button"
            aria-selected={selected}
            disabled={tab.disabled}
            onClick={() => onChange(tab.id)}
            className={cx(
              'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap transition-colors duration-[120ms]',
              'disabled:cursor-not-allowed disabled:opacity-40',
              variant === 'primary'
                ? cx(
                    'h-9 border-b-2 px-3 text-body',
                    selected
                      ? 'border-brand font-medium text-brand'
                      : 'border-transparent text-ink-muted hover:text-ink',
                  )
                : cx(
                    'h-7 rounded-[var(--radius-control)] px-2.5 text-small',
                    selected ? 'bg-brand-soft font-medium text-brand' : 'text-ink-muted hover:bg-surface-sunken',
                  ),
            )}
          >
            {tab.label}
            {tab.count !== undefined ? (
              <span className="tabular font-mono text-badge text-ink-faint">{tab.count}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/* ── Filter bar and saved views ──────────────────────────────────────────────────────────────────── */

export interface SavedView {
  id: string;
  name: string;
  filters: Record<string, string>;
}

/**
 * Saved views live in this browser only, under a key per screen. They are a convenience, not data: losing
 * them costs the user a few clicks, so they never hold anything that matters. Private-window and blocked
 * storage are normal, so every access is guarded.
 */
export function useSavedViews(screenId: string) {
  const key = `rtcom.views.${screenId}`;
  const [views, setViews] = useState<SavedView[]>([]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(key);
      setViews(raw ? (JSON.parse(raw) as SavedView[]) : []);
    } catch {
      setViews([]);
    }
  }, [key]);

  const persist = (next: SavedView[]) => {
    setViews(next);
    try {
      localStorage.setItem(key, JSON.stringify(next));
    } catch {
      // Storage unavailable: the views simply do not survive the session.
    }
  };

  return {
    views,
    save: (name: string, filters: Record<string, string>) =>
      persist([...views.filter((v) => v.name !== name), { id: crypto.randomUUID(), name, filters }]),
    remove: (id: string) => persist(views.filter((v) => v.id !== id)),
  };
}

export function FilterBar({
  children, savedViews, onApplyView, onSaveView, activeCount, onClear,
}: {
  children: ReactNode;
  savedViews?: ReturnType<typeof useSavedViews>;
  onApplyView?: (view: SavedView) => void;
  onSaveView?: () => Record<string, string>;
  activeCount?: number;
  onClear?: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface-sunken px-3 py-2">
      <Filter size={13} className="text-ink-muted" aria-hidden />
      {children}

      {activeCount && activeCount > 0 ? (
        <button
          type="button"
          onClick={onClear}
          className="inline-flex items-center gap-1 rounded-[var(--radius-badge)] bg-brand-soft px-1.5 py-0.5 text-small text-brand"
        >
          {activeCount} filter{activeCount === 1 ? '' : 's'}
          <X size={11} aria-hidden />
        </button>
      ) : null}

      {savedViews && savedViews.views.length > 0 ? (
        <div className="ml-auto flex items-center gap-1.5">
          <SectionLabel>Views</SectionLabel>
          {savedViews.views.map((view) => (
            <button
              key={view.id}
              type="button"
              onClick={() => onApplyView?.(view)}
              className="rounded-[var(--radius-badge)] border border-line bg-surface px-2 py-0.5 text-small hover:bg-brand-soft"
            >
              {view.name}
            </button>
          ))}
        </div>
      ) : null}

      {onSaveView && savedViews ? (
        <Button
          size="dense"
          className={savedViews.views.length > 0 ? '' : 'ml-auto'}
          onClick={() => {
            const name = window.prompt('Name this view');
            if (name) savedViews.save(name, onSaveView());
          }}
        >
          Save view
        </Button>
      ) : null}
    </div>
  );
}

/* ── Timeline: audit and activity ────────────────────────────────────────────────────────────────── */

export interface TimelineEntry {
  id: string;
  at: string;
  title: ReactNode;
  detail?: ReactNode;
  actor?: string | null;
  /** The reason someone gave for the change. It is the most valuable column in an audit trail. */
  reason?: string | null;
  tone?: 'accepted' | 'queued' | 'refused' | 'info' | 'admin' | 'neutral';
}

export function Timeline({ entries, emptyLabel = 'Nothing recorded yet' }: { entries: readonly TimelineEntry[]; emptyLabel?: string }) {
  if (entries.length === 0) {
    return <p className="px-4 py-8 text-center text-small text-ink-muted">{emptyLabel}</p>;
  }

  return (
    <ol className="relative px-4 py-3">
      {/* A single hairline as the spine, rather than a card per event. */}
      <span className="absolute top-3 bottom-3 left-[1.3rem] w-px bg-line" aria-hidden />
      {entries.map((entry) => (
        <li key={entry.id} className="relative flex gap-3 py-2 pl-0">
          <span
            className={cx(
              'relative z-10 mt-1.5 h-2 w-2 shrink-0 rounded-full ring-2 ring-surface',
              entry.tone === 'refused' ? 'bg-refused' : entry.tone === 'queued' ? 'bg-queued' : entry.tone === 'accepted' ? 'bg-accepted' : 'bg-line-strong',
            )}
            aria-hidden
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-3">
              <span className="truncate text-body">{entry.title}</span>
              <time className="shrink-0 font-mono text-badge text-ink-faint" dateTime={entry.at}>
                {fmtDateTime(entry.at)}
              </time>
            </div>
            {entry.detail ? <div className="mt-0.5 text-small text-ink-muted">{entry.detail}</div> : null}
            <div className="mt-0.5 flex flex-wrap gap-x-3 text-small text-ink-faint">
              {entry.actor ? <span>{entry.actor}</span> : null}
              {entry.reason ? <span className="italic">“{entry.reason}”</span> : null}
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}

/* ── Entity 360 ──────────────────────────────────────────────────────────────────────────────────── */

export function Entity360({
  code, title, status, tabs, active, onTabChange, summary, children, rail,
}: {
  /** The record's own identifier, in the mono face. Every code in this ERP is a link to its source. */
  code?: ReactNode;
  title: ReactNode;
  status?: ReactNode;
  tabs: readonly TabDef[];
  active: string;
  onTabChange: (id: string) => void;
  /** The fact strip under the title: the handful of things always worth seeing. */
  summary?: ReactNode;
  children: ReactNode;
  rail?: ReactNode;
}) {
  return (
    <div className="flex min-h-full flex-col">
      <div className="border-b border-line bg-surface px-6 pt-5">
        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0">
            {code ? <div className="code-text text-ink-muted">{code}</div> : null}
            <h1 className="mt-0.5 truncate text-title">{title}</h1>
          </div>
          {status ? <div className="shrink-0 pt-1">{status}</div> : null}
        </div>
        {summary ? <div className="mt-3">{summary}</div> : null}
        <div className="mt-3 -mx-1">
          <Tabs tabs={tabs} active={active} onChange={onTabChange} />
        </div>
      </div>

      <div className="grid min-h-0 flex-1 gap-4 p-6 xl:grid-cols-[minmax(0,1fr)_var(--width-rail)]">
        <div className="min-w-0">{children}</div>
        {rail ? <aside className="min-w-0">{rail}</aside> : null}
      </div>
    </div>
  );
}

export { Panel };
